import { Drawer } from 'components/Drawer'
import Styles from './Actions.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { ChevronRightSmall } from 'blaze-ui/icons/24'
import { Scheduled, Send1 } from 'blaze-ui/icons'
import {
  PostStatusDropdownAction,
  PostStatusDropdownState,
} from 'components/PostStatusDropdown/usePostStatusDropdownState'
import { UserIntent } from 'components/PostStatusDropdown/Types'
import { Button } from 'blaze-ui/Button'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { noop } from 'lodash'
import dayjs from 'dayjs'
import { DrawerProps } from 'components/Drawer/Root'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useState } from 'react'

interface Props extends DrawerProps {
  calendarDispatch: React.Dispatch<PostStatusDropdownAction>
  calendarState: PostStatusDropdownState
  executeAction: (intent: UserIntent, postAt?: string) => Promise<void>
  defaultToCalendar?: boolean
  showUnschedule?: boolean
}

export const PostOrScheduleDrawer = observer(
  ({
    calendarState,
    calendarDispatch,
    executeAction,
    defaultToCalendar = false,
    showUnschedule = false,
    ...props
  }: Props) => {
    const { drawersStore } = useRootStore()
    const [showCalendar, setShowCalendar] = useState(false)
    const [selectedDate, setSelectedDate] = useState(calendarState.selectedDate)
    const calendarOpen = showCalendar || defaultToCalendar
    const close = () => drawersStore.closeDrawer()

    return (
      <Drawer.Root {...props}>
        <Drawer.Portal>
          <Drawer.Content>
            <Drawer.InnerContent>
              <Drawer.Container padding={calendarOpen ? '12px 10px' : '0'}>
                {calendarOpen ? (
                  <DateTimePicker
                    onClose={noop}
                    dateTime={selectedDate ? dayjs(selectedDate) : undefined}
                    inline
                    responsive
                    actions={
                      <div className={Styles.calendarActions}>
                        <Button
                          onPress={() => {
                            executeAction(UserIntent.ScheduleAutoPost, selectedDate)
                            close()
                          }}
                          size="xl"
                          fullWidth
                        >
                          Confirm
                        </Button>
                        <Button
                          fullWidth
                          variant="tertiary"
                          onPress={() => {
                            calendarDispatch({ type: 'CLOSE_CALENDAR' })
                            close()
                          }}
                          size="xl"
                        >
                          Cancel
                        </Button>
                        {showUnschedule && (
                          <Button
                            fullWidth
                            variant="ghost"
                            onPress={() => {
                              executeAction(UserIntent.RemoveReminder)
                              close()
                            }}
                            size="xl"
                          >
                            Unschedule
                          </Button>
                        )}
                      </div>
                    }
                    setDateTime={(datetime) => {
                      if (!datetime) return
                      setSelectedDate(datetime.toISOString())
                    }}
                  />
                ) : (
                  <div className={Styles.postOrSchedule}>
                    <div
                      className={Styles.item}
                      onClick={() => {
                        setShowCalendar(true)
                        calendarDispatch({ type: UserIntent.ScheduleAutoPost })
                      }}
                    >
                      <div className={Styles.left}>
                        <Scheduled />
                        <Text>Schedule for Later</Text>
                      </div>
                      <ChevronRightSmall color="var(--dark-40)" />
                    </div>
                    <div
                      className={Styles.item}
                      onClick={async () => {
                        close()
                        await executeAction(UserIntent.PostNowWithoutConfirmation)
                        executeAction(UserIntent.MarkAsPosted)
                      }}
                    >
                      <div className={Styles.left}>
                        <Send1 />
                        <Text>Post Now</Text>
                      </div>
                      <ChevronRightSmall color="var(--dark-40)" />
                    </div>
                  </div>
                )}
              </Drawer.Container>
            </Drawer.InnerContent>
          </Drawer.Content>
          <Drawer.Overlay />
        </Drawer.Portal>
      </Drawer.Root>
    )
  },
)
